"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { cn } from "@/lib/utils";

// 使用 quality 目录下新图片（public 路径）
const IMAGE_BASE = "/image/new-version/home/quality";

// 特性数据配置
interface FeatureItem {
	titleKey: string;
	descKey: string;
}

// 仅保留 3 个特性项，与文案一致
const featuresData: FeatureItem[] = [
	{
		titleKey: "feature1Title",
		descKey: "feature1Desc",
	},
	{
		titleKey: "feature2Title",
		descKey: "feature2Desc",
	},
	{
		titleKey: "feature3Title",
		descKey: "feature3Desc",
	},
];

// 图片数据：altimeter / 轮廓仪 / 投影仪 / 三维测量
const imagesData = [
	{ src: `${IMAGE_BASE}/altimeter.webp`, alt: "Altimeter" },
	{ src: `${IMAGE_BASE}/contour-measuring-instrument.jpg`, alt: "Contour Measuring Instrument" },
	{ src: `${IMAGE_BASE}/projector.jpg`, alt: "Optical Projector" },
	{ src: `${IMAGE_BASE}/three-dimensional.webp`, alt: "Three-dimensional Measuring" },
];

export default function QualityAssurance({ className }: { className?: string } = {}) {
	const t = useTranslations("qualityAssurance");

	return (
		<section className={cn("bg-[#f5f7fa] py-12 sm:py-16 md:py-20 lg:py-24", className)}>
			<div className="container">
				<div className="flex flex-col gap-8 lg:flex-row">
					{/* 左侧文字内容 */}
					<div className="w-full lg:w-1/2">
						{/* 标题 */}
						<h2 className="mb-4 text-2xl font-bold text-black sm:mb-5 sm:text-3xl md:mb-6 md:text-4xl lg:text-[42px]">
							{t("title")}
						</h2>

						{/* 主体描述 */}
						<p className="mb-8 text-sm leading-[1.6] text-gray-600 sm:mb-10 sm:text-base sm:leading-[1.6] md:mb-[45px] md:text-lg md:leading-[1.6]">
							{t("description")}
						</p>

						{/* 特性列表 */}
						<div className="space-y-6 sm:space-y-8">
							{featuresData.map((feature, index) => (
								<div key={index}>
									<h3 className="mb-3 text-base font-bold text-themePrimary400 sm:mb-4 sm:text-lg md:mb-4 md:text-xl">
										{t(feature.titleKey)}
									</h3>
									<p className="text-sm leading-[1.6] text-gray-900 sm:text-base sm:leading-[1.6] lg:max-w-[90%]">
										{t(feature.descKey)}
									</p>
								</div>
							))}
						</div>
					</div>

					{/* 右侧图片网格 */}
					<div className="w-full lg:w-1/2">
						<div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-x-[30px] md:gap-y-10">
							{imagesData.map((image, index) => (
								<div
									key={index}
									className="group relative overflow-hidden transition-all duration-300 hover:shadow-xl"
								>
									<Image
										src={image.src}
										alt={image.alt}
										width={360}
										height={480}
										className="h-auto w-full transition-transform duration-500 group-hover:scale-105"
										sizes="(max-width: 640px) 45vw, (max-width: 768px) 50vw, (max-width: 1024px) 50vw, 25vw"
									/>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
